import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Shield, Truck, RotateCcw, Award } from "lucide-react";

import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { ScrollFade, StaggerGroup, StaggerItem, HeroStaggerText, Counter } from "@/lib/animations";
import api from "@/lib/api";

const CATEGORY_TILES = [
  { slug: "vegan-pu-leather", name: "Vegan PU Leather", img: "https://images.unsplash.com/photo-1521223890158-f9f7c3d5d504?w=900&q=80" },
  { slug: "puffer", name: "Puffer", img: "https://images.unsplash.com/photo-1614252369475-531eba835eb1?w=900&q=80" },
  { slug: "suede", name: "Suede", img: "https://images.pexels.com/photos/7026422/pexels-photo-7026422.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { slug: "corduroy", name: "Corduroy", img: "https://images.unsplash.com/photo-1578198576866-7e0ba6078128?w=900&q=80" },
  { slug: "cotton", name: "Cotton", img: "https://images.pexels.com/photos/15396619/pexels-photo-15396619.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { slug: "windcheater", name: "Windcheater", img: "https://images.unsplash.com/photo-1619102814948-e164e584cf0c?w=900&q=80" },
];

const MARQUEE = [
  "Vegan PU Leather", "Vegan PU Fur Leather", "Suede", "Corduroy", "Cotton", "Puffer", "Polyester", "Windcheater",
];

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [featured, setFeatured] = useState([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    api.get("/products?bestseller=true&limit=8").then(r => setBestsellers(r.data || [])).catch(() => {});
    api.get("/products?featured=true&limit=4").then(r => setFeatured(r.data || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative min-h-[88vh] bg-[#163A2F] text-[#F8F6F0] overflow-hidden grain">
        <motion.div
          initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 0.55 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src="https://images.unsplash.com/photo-1619603364937-8d7af41ef206?w=1800&q=80"
            alt="Norbu hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1C16]/85 via-[#163A2F]/55 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 min-h-[88vh] flex flex-col justify-center pt-16 pb-24">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#C9A227]" />
            <span className="overline text-[#C9A227]">Autumn / Winter '26</span>
          </motion.div>

          <HeroStaggerText
            text="The Jacket, Reimagined."
            className="mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[110px] font-extralight leading-[0.95] tracking-tight max-w-5xl"
          />
          <HeroStaggerText
            text="Made in India."
            className="mt-2 text-5xl sm:text-6xl md:text-7xl lg:text-[110px] font-extralight italic text-[#C9A227] leading-[0.95] tracking-tight"
            delay={0.5}
          />

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 max-w-xl text-[#F8F6F0]/80 font-light text-base sm:text-lg leading-relaxed"
          >
            Nine considered categories of premium jackets. Cruelty-free leathers, refined suedes and engineered shells — built for the contemporary Indian gentleman.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              className="bg-[#C9A227] text-[#0A1C16] px-9 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#DCC267] transition-colors flex items-center gap-2"
            >
              Shop the Collection <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/category/puffer"
              className="border border-[#F8F6F0]/40 text-[#F8F6F0] px-9 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#F8F6F0] hover:text-[#163A2F] transition-colors"
            >
              Explore Puffer
            </Link>
          </motion.div>
        </div>

        {/* scroll indicator */}
        {!reduce && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          >
            <div className="text-[#F8F6F0]/60 overline">Scroll</div>
            <motion.div
              animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}
              className="w-px h-10 mx-auto bg-[#C9A227] mt-2"
            />
          </motion.div>
        )}
      </section>

      {/* MARQUEE BAND */}
      <section className="bg-[#F8F6F0] border-y hairline py-5 overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap gap-12">
          {[...MARQUEE, ...MARQUEE].map((c, i) => (
            <span key={i} className="overline text-[#163A2F] flex items-center gap-12">
              {c} <span className="text-[#C9A227]">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="bg-[#F8F6F0] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Categories</div>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-light text-[#163A2F] tracking-tight">
              Nine fabrications.<br />
              <span className="italic font-extralight text-[#C9A227]">One obsession.</span>
            </h2>
          </ScrollFade>

          <StaggerGroup className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {CATEGORY_TILES.map((c, i) => (
              <StaggerItem key={c.slug}>
                <Link to={`/category/${c.slug}`} data-testid={`category-tile-${c.slug}`} className="group block relative overflow-hidden aspect-[4/5]">
                  <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.08]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C16]/85 via-[#0A1C16]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 text-[#F8F6F0]">
                    <div className="overline text-[#C9A227] mb-2">0{i + 1}</div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl md:text-2xl font-light">{c.name}</h3>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <ScrollFade delay={0.1} className="mt-10 text-center">
            <Link to="/shop" className="link-gold overline text-[#163A2F]">View all categories →</Link>
          </ScrollFade>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="bg-[#F8F6F0] py-24 md:py-32 border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="flex items-end justify-between mb-12 gap-6">
              <div>
                <div className="overline text-[#C9A227]">Most loved</div>
                <h2 className="mt-3 text-4xl md:text-5xl font-light text-[#163A2F] tracking-tight">Best Sellers</h2>
              </div>
              <Link to="/shop" className="hidden md:inline-block link-gold overline text-[#163A2F]">Shop all →</Link>
            </div>
          </ScrollFade>

          <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {bestsellers.slice(0, 8).map((p, i) => (
              <StaggerItem key={p.id}><ProductCard product={p} index={i} /></StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* STATS / NUMBERS */}
      <section className="bg-[#163A2F] text-[#F8F6F0] grain relative py-24 md:py-32">
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Our promise</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-tight max-w-3xl">
              Quality you can <span className="italic text-[#C9A227]">verify.</span>
            </h2>
          </ScrollFade>

          <StaggerGroup className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-10">
            {[
              { n: 9,   label: "Premium fabrications",   suffix: "" },
              { n: 100, label: "Made in India",          suffix: "%" },
              { n: 24,  label: "Hour reply guarantee",   suffix: "h" },
              { n: 2,   label: "Year stitching warranty", suffix: "yr" },
            ].map((s, i) => (
              <StaggerItem key={i}>
                <div className="text-5xl md:text-6xl font-extralight text-[#C9A227] tracking-tight">
                  <Counter to={s.n} suffix={s.suffix} />
                </div>
                <div className="mt-3 overline text-[#F8F6F0]/70">{s.label}</div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="bg-[#F8F6F0] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <ScrollFade className="relative aspect-[4/5] overflow-hidden">
            <img src="https://images.pexels.com/photos/6626749/pexels-photo-6626749.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Editorial" className="w-full h-full object-cover" />
          </ScrollFade>
          <ScrollFade delay={0.1}>
            <div className="overline text-[#C9A227]">The Norbu Edit</div>
            <h2 className="mt-4 text-4xl md:text-5xl font-light text-[#163A2F] tracking-tight">
              Considered cuts.<br />
              <span className="italic text-[#C9A227] font-extralight">Quietly luxurious.</span>
            </h2>
            <p className="mt-6 text-[#163A2F]/75 font-light leading-relaxed max-w-lg">
              Every Norbu piece begins with the fabric and ends with a wearer who feels seen. We obsess over hand, drape, fit and finish — so you can simply put it on and go.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-y-6 gap-x-10 max-w-md">
              {[
                { icon: Award, t: "Premium hardware" },
                { icon: Shield, t: "2-year stitching" },
                { icon: Truck, t: "Fast pan-India" },
                { icon: RotateCcw, t: "Easy returns" },
              ].map(({ icon: Icon, t }, i) => (
                <div key={i} className="flex items-center gap-3 text-[#163A2F]">
                  <Icon className="w-5 h-5 text-[#C9A227]" strokeWidth={1.5} />
                  <span className="text-sm font-light">{t}</span>
                </div>
              ))}
            </div>
            <Link to="/shop" className="mt-10 inline-block bg-[#163A2F] text-white px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#0A1C16] transition-colors">
              Shop the Edit
            </Link>
          </ScrollFade>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[#F8F6F0] py-24 md:py-32 border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Featured</div>
            <h2 className="mt-3 text-4xl md:text-5xl font-light text-[#163A2F] tracking-tight">New this season</h2>
          </ScrollFade>
          <StaggerGroup className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {featured.slice(0, 4).map((p) => (
              <StaggerItem key={p.id}><ProductCard product={p} /></StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#0A1C16] text-[#F8F6F0] py-24 grain relative">
        <div className="relative z-10 max-w-[800px] mx-auto px-6 text-center">
          <ScrollFade>
            <div className="overline text-[#C9A227]">Join the list</div>
            <h2 className="mt-4 text-3xl md:text-4xl font-light tracking-tight">
              Early access. Private drops.<br />
              <span className="italic text-[#C9A227] font-extralight">No noise.</span>
            </h2>
            <form className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" onSubmit={(e) => { e.preventDefault(); }}>
              <input
                type="email" required placeholder="your@email.com"
                data-testid="newsletter-email"
                className="flex-1 bg-transparent border border-[#F8F6F0]/30 px-5 py-4 text-[#F8F6F0] placeholder-[#F8F6F0]/40 focus:outline-none focus:border-[#C9A227]"
              />
              <button type="submit" data-testid="newsletter-submit" className="bg-[#C9A227] text-[#0A1C16] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#DCC267] transition-colors">Subscribe</button>
            </form>
          </ScrollFade>
        </div>
      </section>
    </Layout>
  );
}
